// @ts-nocheck
import React, { useState, useEffect, useRef, useCallback, useContext } from 'react';
import {
  getChats,
  getAllBookings,
  getChatMessages,
  getChatUnreadCount,
  createChat,
  sendChatMessage,
  markChatMessagesRead,
  ChatMessage
} from './services/apiService';
import { DataContext } from './App';
import authService from './authService';

const CHAT_SETTINGS_URL = 'https://yachtmanagementsuite.com/api/chat-settings.php';

// Converted Message type for internal use (matching old interface)
interface Message {
  id: string;
  chatId: string;
  sender: 'CUSTOMER' | 'TECHNICAL' | 'FINANCIAL' | 'BOOKING' | 'ADMIN';
  senderName: string;
  message: string;
  timestamp: string;
  read: boolean;
  category: 'TECHNICAL' | 'FINANCIAL' | 'BOOKING';
}

// Web Push: VAPID public key + base64url -> Uint8Array (για applicationServerKey)
const VAPID_PUBLIC = 'BM40-tBRoZrrdSSx9AYo4bMs26dZphA6L_RGJh9m5pee6imrEhvyIl3NgdltSloAbxqPAG_mmMhu5_edFTYdqrY';
function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) { outputArray[i] = rawData.charCodeAt(i); }
  return outputArray;
}

// Convert API message to internal format
const convertApiMessage = (msg: ChatMessage): Message => ({
  id: String(msg.id || msg.chat_id + '-' + msg.timestamp),
  chatId: msg.chat_id,
  sender: msg.sender_role,
  senderName: msg.sender_name,
  message: msg.message,
  timestamp: msg.timestamp || new Date().toISOString(),
  read: msg.read_status === 1,
  category: msg.category
});

export default function FloatingChatWidget() {
  // Get context data (API is source of truth)
  const contextData = useContext(DataContext);

  // Logged-in staff (if any) -- staff see a different view than customers
  const currentStaff = authService.getCurrentUser();
  const isStaff = !!currentStaff?.code;
  // Customer's chosen language (flag selector). Staff always use Greek.
  const myLang = (typeof sessionStorage !== 'undefined' && sessionStorage.getItem('yacht_lang')) || 'el';

  const [isOpen, setIsOpen] = useState(false);
  const [staffTab, setStaffTab] = useState('chats'); // 'chats' | 'new'
  const [staffList, setStaffList] = useState([]);
  const [selectedStaffChat, setSelectedStaffChat] = useState(null);
  const [staffMessages, setStaffMessages] = useState([]);
  const [staffInput, setStaffInput] = useState('');
  const [staffUnread, setStaffUnread] = useState({}); // { chatId: count }
  const [showSoundSettings, setShowSoundSettings] = useState(false);
  const [soundList, setSoundList] = useState([]);
  const [mySound, setMySound] = useState('');
  const [myMuted, setMyMuted] = useState(false);
  const [custSearch, setCustSearch] = useState('');
  const [custResults, setCustResults] = useState([]);
  const [selectedCustChat, setSelectedCustChat] = useState(null);
  const [custMessages, setCustMessages] = useState([]);
  const [custInput, setCustInput] = useState('');
  const [custBookings, setCustBookings] = useState([]);
  const [allChats, setAllChats] = useState([]);
  const [selectedConvo, setSelectedConvo] = useState(null);
  const [convoMessages, setConvoMessages] = useState([]);
  const [convoInput, setConvoInput] = useState('');
  const [newMode, setNewMode] = useState('staff'); // 'staff' | 'cust'
  const [messages, setMessages] = useState<Message[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [chatId, setChatId] = useState<string | null>(null);
  const [sending, setSending] = useState(false);
  const [customerName, setCustomerName] = useState('');
  const [unreadCount, setUnreadCount] = useState(0);
  const [selectedCategory, setSelectedCategory] = useState<'TECHNICAL' | 'FINANCIAL' | 'BOOKING' | 'ADMINISTRATOR' | null>(null);
  const [isVisitor, setIsVisitor] = useState(false);
  const [bookingCode, setBookingCode] = useState('');
  const [vesselName, setVesselName] = useState('');
  const [loading, setLoading] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const pollingRef = useRef<NodeJS.Timeout | null>(null);
  const mySoundRef = useRef('');
  const myMutedRef = useRef(false);
  const audioUnlockedRef = useRef(false);
  const pushRegisteredRef = useRef(false);
  const lastRegisteredAsStaffRef = useRef(false); // τι γράφτηκε τελευταία: staff (true) ή VISITOR/customer (false)

  // Get booking info from context (API is source of truth)
  useEffect(() => {
    let code = '';
    let vessel = '';
    let name = 'Guest Customer';
    let visitor = true;

    // Try to get booking info from context
    const urlBooking = new URLSearchParams(window.location.search).get('booking');
    const rawCode = contextData?.bookingNumber || urlBooking || localStorage.getItem('currentBooking') || '';
    const currentBookingCode = rawCode ? String(rawCode).replace(/^[^0-9]*/, '').trim() : '';
    if (currentBookingCode) {
      // Having a booking code is enough to be a real customer (not a visitor).
      code = currentBookingCode;
      visitor = false;
      // Find booking in globalBookings from context -- ONLY to enrich vessel/name.
      const globalBookings = contextData?.globalBookings || [];
      const stripNum = (v: any) => String(v || '').replace(/^[^0-9]*/, '').trim();
      const booking = globalBookings.find((b: any) =>
        stripNum(b.bookingNumber) === currentBookingCode ||
        stripNum(b.code) === currentBookingCode
      ) || contextData?.data;

      if (booking) {
        vessel = booking.vesselName || booking.selectedVessel || '';
        name = `${booking.skipperFirstName || ''} ${booking.skipperLastName || ''}`.trim()
          || `${booking.chartererFirstName || ''} ${booking.chartererLastName || ''}`.trim()
          || 'Guest Customer';
      }
    }

    // If no booking, create/get guest code
    if (!code) {
      const guestCode = localStorage.getItem('guestBookingCode');
      if (guestCode) {
        code = guestCode;
      } else {
        code = 'VISITOR-' + Date.now();
        localStorage.setItem('guestBookingCode', code);
      }
      vessel = 'General Inquiry';
      visitor = true;
    }

    setBookingCode(code);
    setVesselName(vessel);
    setCustomerName(name);
    setIsVisitor(visitor);
  }, [contextData?.bookingNumber, contextData?.globalBookings, contextData?.data, window.location.search]);

  // Global unread counter - polling every 30 seconds
  useEffect(() => {
    if (!bookingCode) return;

    const updateUnreadCount = async () => {
      try {
        const result = await getChatUnreadCount({ booking_code: bookingCode });
        if (result.success) {
          setUnreadCount(result.unread_count);
        }
      } catch (e) {
        console.error('Error fetching unread count:', e);
      }
    };

    updateUnreadCount();
    const interval = setInterval(updateUnreadCount, 4000); // 4 seconds (badge live even when closed)
    return () => clearInterval(interval);
  }, [bookingCode]);

  // KOMMATI 3: load available sounds + my saved settings when the widget opens.
  useEffect(() => {
    if (!isStaff && !isOpen) return;
    const userKey = isStaff ? (currentStaff?.code || '') : bookingCode;
    let cancelled = false;
    (async () => {
      try {
        const sres = await fetch(CHAT_SETTINGS_URL + '?action=sounds');
        const sj = await sres.json();
        if (!cancelled && sj && sj.success) setSoundList(sj.sounds || []);
      } catch (e) {}
      if (!userKey) return;
      try {
        const gres = await fetch(CHAT_SETTINGS_URL + '?action=get&user_key=' + encodeURIComponent(userKey));
        const gj = await gres.json();
        if (!cancelled && gj && gj.success) {
          setMySound(gj.notification_sound || '');
          setMyMuted(Number(gj.muted) === 1);
        }
      } catch (e) {}
    })();
    return () => { cancelled = true; };
  }, [isOpen, bookingCode, isStaff]);

  // Keep refs in sync so playNotify (called from intervals) reads fresh values.
  useEffect(() => { mySoundRef.current = mySound; myMutedRef.current = myMuted; }, [mySound, myMuted]);

  // Auto-select BOOKING category for visitors (no charter code)
  useEffect(() => {
    if (isOpen && isVisitor && !selectedCategory) {
      // Visitors auto-route to BOOKING manager
      setSelectedCategory('BOOKING');
    }
  }, [isOpen, isVisitor, selectedCategory]);

  // Customer (non-visitor): auto-open the category that has unread (probe each category).
  useEffect(() => {
    if (!isOpen || isVisitor || isStaff || selectedCategory || !bookingCode) return;
    let cancelled = false;
    (async () => {
      const cats = ['TECHNICAL', 'FINANCIAL', 'BOOKING', 'ADMINISTRATOR'];
      for (const cat of cats) {
        try {
          const res = await getChatMessages(bookingCode + '-' + cat, undefined, bookingCode, myLang);
          if (cancelled) return;
          if (res && res.success) {
            const unread = (res.messages || []).filter((m) => Number(m.read_status) === 0 && m.sender_role !== 'CUSTOMER').length;
            if (unread > 0) { setSelectedCategory(cat); return; }
          }
        } catch (e) {}
      }
    })();
    return () => { cancelled = true; };
  }, [isOpen, isVisitor, isStaff, selectedCategory, bookingCode]);

  // Initialize chat when category is selected
  useEffect(() => {
    if (!selectedCategory || !bookingCode) return;

    const initChat = async () => {
      setLoading(true);
      try {
        // Create or get existing chat
        const result = await createChat({
          booking_code: bookingCode,
          vessel_name: vesselName,
          customer_name: customerName,
          category: selectedCategory,
          is_visitor: isVisitor ? 1 : 0
        });

        if (result.success && result.chat) {
          setChatId(result.chat.chat_id);

          // Load messages
          const msgResult = await getChatMessages(result.chat.chat_id, undefined, bookingCode, myLang);
          if (msgResult.success) {
            setMessages(msgResult.messages.map(convertApiMessage));
          }
        }
      } catch (error) {
        console.error('Error initializing chat:', error);
      } finally {
        setLoading(false);
      }
    };

    initChat();
  }, [selectedCategory, bookingCode, vesselName, customerName, isVisitor]);

  // Poll for new messages when chat is open (every 4 seconds) + beep on new staff reply
  useEffect(() => {
    if (!isOpen || !chatId) return;

    const pollMessages = async () => {
      try {
        const result = await getChatMessages(chatId, undefined, bookingCode, myLang);
        if (result.success) {
          const mapped = result.messages.map(convertApiMessage);
          setMessages((prev) => {
            const hasNew = mapped.length !== prev.length
              && mapped.some((m) => m.sender !== 'CUSTOMER' && !prev.find((pm) => String(pm.id) === String(m.id)));
            if (hasNew) { try { playNotify(); } catch (e) {} }
            return mapped;
          });
        }
      } catch (e) {
        console.error('Error polling messages:', e);
      }
    };

    pollingRef.current = setInterval(pollMessages, 4000); // 4 seconds

    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
      }
    };
  }, [isOpen, chatId, bookingCode]);

  // Mark messages as read when chat is opened
  useEffect(() => {
    if (isOpen && chatId && selectedCategory) {
      markChatMessagesRead(chatId, 'CUSTOMER', bookingCode);

      // Update unread count
      setTimeout(async () => {
        const result = await getChatUnreadCount({ booking_code: bookingCode });
        if (result.success) {
          setUnreadCount(result.unread_count);
        }
      }, 500);
    }
  }, [isOpen, chatId, selectedCategory, bookingCode]);

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    unlockAudio();

    if (!newMessage.trim() || !chatId || sending || !selectedCategory) return;

    setSending(true);

    try {
      const result = await sendChatMessage({
        chat_id: chatId,
        booking_code: bookingCode,
        vessel_name: vesselName,
        customer_name: customerName,
        sender_id: bookingCode,
        sender_name: customerName,
        sender_role: 'CUSTOMER',
        recipient_role: (selectedCategory === 'ADMINISTRATOR') ? 'ADMIN' : selectedCategory, // ADMINISTRATOR -> ADMIN (recipient_role constraint)
        category: selectedCategory,
        lang: myLang,
        message: newMessage.trim(),
        is_visitor: isVisitor ? 1 : 0
      });

      if (result.success && result.message) {
        setMessages((prev: Message[]) => [...prev, convertApiMessage(result.message)]);
        setNewMessage('');
        setTimeout(scrollToBottom, 100);
      }
    } catch (error) {
      console.error('Error sending message:', error);
      alert('Failed to send. Please try again.');
    } finally {
      setSending(false);
    }
  };

  const handleBackToCategories = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    // Reset chat state
    setSelectedCategory(null);
    setMessages([]);
    setChatId(null);
    setNewMessage('');
  };

  // ===== Staff-to-staff chat (KOMMATI 2) =====
  const roleToChat = (role) => (role === 'ACCOUNTING' ? 'FINANCIAL' : (role || ''));

  // ===== KOMMATI 4b: unread / beep / auto-open =====
  // Unlock audio on a CUSTOMER user-gesture so timer-driven playNotify can play
  // (mobile/Safari block new Audio().play() outside a gesture). Staff are fine already.
  // Web Push subscribe (πελάτης ΚΑΙ staff) — silent, να μη σπάει τίποτα αν αποτύχει.
  // Role για staff = roleToChat(currentStaff.role) ώστε να ταιριάζει με το recipient_role
  // που χρησιμοποιεί το chat (π.χ. ACCOUNTING -> FINANCIAL) — αλλιώς δεν φτάνει το push.
  const registerPush = async () => {
    try {
      // 🔔 PUSH: effective staff = ενεργή session (sessionStorage) Ή persistent cookie (επιβιώνει του κλεισίματος).
      // Έτσι συσκευή που είναι staff γράφεται TECHNICAL μετά reopen, ΟΧΙ VISITOR.
      const effStaff = currentStaff || authService.getStaffPushIdentity();
      if (pushRegisteredRef.current) return;        // ήδη επιτυχημένο subscribe σε αυτή τη συνεδρία
      if (!effStaff && !bookingCode) return;         // customer χωρίς booking -> retry όταν φορτώσει (ΧΩΡΙΣ να σετάρει guard)
      if (!('serviceWorker' in navigator) || !('PushManager' in window) || !('Notification' in window)) return;
      let perm = Notification.permission;
      if (perm === 'default') { perm = await Notification.requestPermission(); } // ΠΡΩΤΟ, πριν τα serviceWorker awaits (Safari/iOS gesture)
      if (perm !== 'granted') return;
      const reg = await navigator.serviceWorker.ready;
      // Κράτα το ΥΠΑΡΧΟΝ valid subscription (το fcm/send/ είναι το ΚΑΝΟΝΙΚΟ Chrome/Android format, ΟΧΙ legacy).
      // Σταθερό endpoint ανά συσκευή -> ON CONFLICT(endpoint) DO UPDATE -> συσκευή = τελευταίος logged-in. Όχι churn/orphans.
      let sub = await reg.pushManager.getSubscription();
      if (!sub) {
        sub = await reg.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC),
        });
      }
      const body = {
        endpoint: sub.endpoint,
        keys: (sub.toJSON() as any).keys,
        user_key: effStaff ? effStaff.code : bookingCode,
        booking_code: effStaff ? '' : bookingCode,
        role: effStaff ? roleToChat(effStaff.role) : 'CUSTOMER',
      };
      await fetch('https://yachtmanagementsuite.com/api/push-subscribe.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      pushRegisteredRef.current = true; // guard ΜΟΝΟ μετά από επιτυχές POST
      lastRegisteredAsStaffRef.current = !!effStaff; // θυμήσου αν γράφτηκε ως staff ή VISITOR (για upgrade μετά login)
    } catch (e) { /* silent — push failure δεν επηρεάζει το chat */ }
  };

  const unlockAudio = () => {
    registerPush(); // ο guard ελέγχεται ΜΕΣΑ στο registerPush
    if (audioUnlockedRef.current) return;
    try {
      const a = new Audio('/sounds/' + (mySoundRef.current || 'liecio-message-notification-190034.mp3'));
      a.muted = true;
      a.play().then(() => { a.pause(); a.currentTime = 0; audioUnlockedRef.current = true; }).catch(() => {});
    } catch (e) {}
  };

  // Customer: αν άνοιξε το chat πριν φορτώσει το booking, κάνε subscribe ΜΟΛΙΣ φορτώσει το bookingCode.
  // ΟΧΙ σε staff-cookie συσκευή (να μη γραφτεί VISITOR ενώ είναι TECHNICAL).
  useEffect(() => { if (!isStaff && !authService.getStaffPushIdentity() && bookingCode) { registerPush(); } }, [bookingCode]);

  // Staff upgrade: αν έγινε login (isStaff false->true) ΑΛΛΑ η τελευταία εγγραφή ήταν VISITOR/customer,
  // ξεκλείδωσε τον guard και ξαναγράψε ως staff (TECHNICAL/ADMIN). Λύνει το «guard κλειδωμένο στο VISITOR».
  useEffect(() => {
    const staffId = currentStaff || authService.getStaffPushIdentity();
    if (staffId && !lastRegisteredAsStaffRef.current) {
      pushRegisteredRef.current = false; // ξεκλείδωσε
      registerPush();                    // ξαναγράψε ως staff
    }
  }, [isStaff]); // eslint-disable-line react-hooks/exhaustive-deps

  const playNotify = () => {
    try {
      if (myMutedRef.current) return;
      const file = mySoundRef.current || 'liecio-message-notification-190034.mp3';
      const a = new Audio('/sounds/' + file);
      a.play().catch(() => {});
    } catch (e) {}
  };
  const staffChatIdFor = (peerCode) => 'staff-' + [currentStaff.code, peerCode].sort().join('-');

  // Fetch a FRESH staff list from DB whenever a staff member opens the widget (no cache).
  useEffect(() => {
    if (!isStaff) return;
    let cancelled = false;
    (async () => {
      try {
        const all = await authService.fetchEmployeesFromAPI();
        if (cancelled) return;
        const list = (all || []).filter(
          (e) => e.enabled !== false && e.code !== currentStaff.code && e.code !== 'CHECKIN2025'
        );
        setStaffList(list);
      } catch (err) {
        console.error('Error fetching staff list:', err);
      }
    })();
    return () => { cancelled = true; };
  }, [isStaff]);

  const openStaffChat = async (employee) => {
    const ids = [currentStaff.code, employee.code].sort();
    const sChatId = 'staff-' + ids[0] + '-' + ids[1];
    try {
      await createChat({
        chat_id: sChatId,
        booking_code: sChatId,
        category: 'ADMINISTRATOR',
        is_visitor: 0,
        customer_name: employee.name
      });
      setSelectedConvo({
        chatId: sChatId,
        name: employee.name,
        isStaff: true,
        category: 'ADMINISTRATOR',
        bookingCode: sChatId,
        vessel: ''
      });
      setStaffTab('chats');
      const msgRes = await getChatMessages(sChatId, undefined, undefined, 'el');
      setConvoMessages(msgRes.success ? (msgRes.messages || []) : []);
      // Mark the peer's messages as read and clear the unread badge immediately.
      await markChatMessagesRead(sChatId, currentStaff.code);
      setStaffUnread((prev) => ({ ...prev, [sChatId]: 0 }));
    } catch (err) {
      console.error('Error opening staff chat:', err);
    }
  };

  const handleSendStaffMessage = async (e) => {
    e.preventDefault();
    if (!staffInput.trim() || !selectedStaffChat) return;
    try {
      const result = await sendChatMessage({
        chat_id: selectedStaffChat.chatId,
        booking_code: selectedStaffChat.chatId,
        sender_id: currentStaff.code,
        sender_name: currentStaff.name,
        sender_role: roleToChat(currentStaff.role),
        recipient_role: roleToChat(selectedStaffChat.peer.role),
        category: 'ADMINISTRATOR',
        lang: 'el',
        message: staffInput.trim(),
        is_visitor: 0
      });
      if (result.success && result.message) {
        setStaffMessages((prev) => [...prev, result.message]);
        setStaffInput('');
      }
    } catch (err) {
      console.error('Error sending staff message:', err);
    }
  };

  // KOMMATI 5a: poll ALL my conversations (customers of my category + my staff chats).
  useEffect(() => {
    if (!isStaff) return;
    let cancelled = false;
    let prevTotal = -1;
    const myCat = roleToChat(currentStaff.role);
    const isAdminLike = (myCat === 'ADMIN' || myCat === 'OWNER');
    const poll = async () => {
      try {
        const res = await getChats({});
        if (cancelled || !res || !res.success) return;
        const mine = (res.chats || []).filter((c) => {
          const cid = String(c.chat_id || '');
          if (cid.indexOf('staff-') === 0) return cid.indexOf(currentStaff.code) !== -1;
          if (isAdminLike) return true;
          return c.category === myCat;
        });
        mine.sort((a, b) => new Date(b.last_message_at || 0).getTime() - new Date(a.last_message_at || 0).getTime());
        setAllChats(mine);
        const map = {};
        let total = 0;
        mine.forEach((c) => { const n = Number(c.unread_count) || 0; map[c.chat_id] = n; total += n; });
        setStaffUnread(map);
        if (prevTotal !== -1 && total > prevTotal && !selectedConvo) { playNotify(); }
        prevTotal = total;
      } catch (err) {
        console.error('Error polling conversations:', err);
      }
    };
    poll();
    const interval = setInterval(poll, 4000);
    return () => { cancelled = true; clearInterval(interval); };
  }, [isStaff, isOpen, staffTab, selectedConvo]);

  // KOMMATI 4b: poll messages inside the open staff chat; beep on incoming from peer.
  useEffect(() => {
    if (!isStaff || !selectedStaffChat) return;
    let cancelled = false;
    const poll = async () => {
      try {
        const res = await getChatMessages(selectedStaffChat.chatId, undefined, undefined, 'el');
        if (cancelled || !res || !res.success) return;
        const msgs = res.messages || [];
        setStaffMessages((prev) => {
          const hasNewFromPeer = msgs.length !== prev.length
            && msgs.some((m) => m.sender_id !== currentStaff.code
              && !prev.find((pm) => String(pm.id) === String(m.id)));
          if (hasNewFromPeer) { try { playNotify(); } catch (e) {} }
          return msgs;
        });
      } catch (err) {
        console.error('Error polling staff messages:', err);
      }
    };
    const interval = setInterval(poll, 3000);
    return () => { cancelled = true; clearInterval(interval); };
  }, [isStaff, selectedStaffChat]);

  const saveSoundSettings = async () => {
    const userKey = isStaff ? (currentStaff?.code || '') : bookingCode;
    if (!userKey) { setShowSoundSettings(false); return; }
    try {
      await fetch(CHAT_SETTINGS_URL + '?action=set', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_key: userKey, notification_sound: mySound, muted: myMuted ? 1 : 0 })
      });
    } catch (e) {
      console.error('Error saving sound settings:', e);
    }
    setShowSoundSettings(false);
  };

  const previewSound = (name) => {
    try { const a = new Audio('/sounds/' + name); a.play().catch(() => {}); } catch (e) {}
  };

  // ===== KOMMATI 3: Customers tab (staff -> customer) =====
  const custField = (b, k) => {
    if (!b) return '';
    if (b[k] !== undefined && b[k] !== null && b[k] !== '') return b[k];
    if (b.booking_data && b.booking_data[k] != null) return b.booking_data[k];
    return '';
  };
  const custNum = (b) => String(custField(b, 'bookingNumber') || (b && b.booking_number) || (b && b.bookingCode) || (b && b.charterCode) || (b && b.code) || '').replace(/^[^0-9]*/, '').trim();
  const stripNum = (x) => String(x || '').replace(/^[^0-9]*/, '');

  // Fetch bookings once when entering the Customers tab if context has none.
  useEffect(() => {
    if (!isStaff || staffTab !== 'new') return;
    const ctx = contextData?.globalBookings || [];
    if (ctx.length || custBookings.length) return;
    let cancelled = false;
    (async () => {
      try {
        const bs = await getAllBookings();
        if (!cancelled) setCustBookings(bs || []);
      } catch (e) {}
    })();
    return () => { cancelled = true; };
  }, [isStaff, staffTab]);

  // Compute search results from bookings (number / charterer / skipper / vessel).
  useEffect(() => {
    if (!isStaff || staffTab !== 'new') return;
    const ctx = contextData?.globalBookings || [];
    const src = ctx.length ? ctx : custBookings;
    const q = custSearch.trim().toLowerCase();
    if (!q) { setCustResults([]); return; }
    const qDigits = q.replace(/[^0-9]/g, '');
    const out = (src || []).filter((b) => {
      const num = stripNum(custNum(b)).toLowerCase();
      const ch = (custField(b, 'chartererFirstName') + ' ' + custField(b, 'chartererLastName')).trim().toLowerCase();
      const sk = (custField(b, 'skipperFirstName') + ' ' + custField(b, 'skipperLastName')).trim().toLowerCase();
      const ves = String(custField(b, 'vesselName') || '').toLowerCase();
      return (qDigits && num.indexOf(qDigits) !== -1)
        || (ch && ch.indexOf(q) !== -1)
        || (sk && sk.indexOf(q) !== -1)
        || (ves && ves.indexOf(q) !== -1);
    }).slice(0, 15);
    setCustResults(out);
  }, [custSearch, staffTab, isStaff, custBookings, contextData?.globalBookings]);

  const openCustomerChat = async (booking, peerName) => {
    const bnum = custNum(booking);
    const vessel = String(custField(booking, 'vesselName') || '');
    // Thread/category is derived from the STAFF member's OWN role (not a manual choice).
    let cat = roleToChat(currentStaff.role) || 'BOOKING';
    if (cat === 'ADMIN') cat = 'ADMINISTRATOR'; // category/chat_id use ADMINISTRATOR; recipient_role maps back via catToRecip
    try {
      // chat_id mirrors the customer widget: <booking_code>-<CATEGORY>.
      const result = await createChat({
        booking_code: bnum,
        vessel_name: vessel,
        customer_name: peerName,
        category: cat,
        is_visitor: 0
      });
      const cid = (result && result.chat && result.chat.chat_id) ? result.chat.chat_id : (bnum + '-' + cat);
      setSelectedConvo({
        chatId: cid,
        name: peerName,
        isStaff: false,
        category: cat,
        bookingCode: bnum,
        vessel
      });
      setStaffTab('chats');
      const msgRes = await getChatMessages(cid, undefined, undefined, 'el');
      setConvoMessages(msgRes.success ? (msgRes.messages || []) : []);
      await markChatMessagesRead(cid, currentStaff.code);
    } catch (e) {
      console.error('Error opening customer chat:', e);
    }
  };

  const handleSendCustMessage = async (e) => {
    e.preventDefault();
    if (!custInput.trim() || !selectedCustChat) return;
    try {
      const result = await sendChatMessage({
        chat_id: selectedCustChat.chatId,
        booking_code: selectedCustChat.bookingCode,
        vessel_name: selectedCustChat.vessel,
        customer_name: selectedCustChat.peerName,
        sender_id: currentStaff.code,
        sender_name: currentStaff.name,
        sender_role: roleToChat(currentStaff.role),
        recipient_role: 'BOOKING',
        category: 'BOOKING',
        lang: 'el',
        message: custInput.trim(),
        is_visitor: 0
      });
      if (result.success && result.message) {
        setCustMessages((prev) => [...prev, result.message]);
        setCustInput('');
      }
    } catch (err) {
      console.error('Error sending customer message:', err);
    }
  };

  // Poll messages in the open customer chat; beep on incoming from the customer.
  useEffect(() => {
    if (!isStaff || !selectedCustChat) return;
    let cancelled = false;
    const poll = async () => {
      try {
        const res = await getChatMessages(selectedCustChat.chatId, undefined, undefined, 'el');
        if (cancelled || !res || !res.success) return;
        const msgs = res.messages || [];
        setCustMessages((prev) => {
          const hasNew = msgs.length !== prev.length
            && msgs.some((m) => m.sender_id !== currentStaff.code
              && !prev.find((pm) => String(pm.id) === String(m.id)));
          if (hasNew) { try { playNotify(); } catch (e) {} }
          return msgs;
        });
      } catch (err) {
        console.error('Error polling customer messages:', err);
      }
    };
    const interval = setInterval(poll, 3000);
    return () => { cancelled = true; clearInterval(interval); };
  }, [isStaff, selectedCustChat]);

  // ===== KOMMATI 5a: unified conversation (customers + staff) =====
  const catToRecip = (cat) => (cat === 'ADMINISTRATOR' ? 'ADMIN' : (cat || 'BOOKING'));

  const openExistingChat = async (chat) => {
    const cid = chat.chat_id;
    const isStaffChat = String(cid || '').indexOf('staff-') === 0;
    const name = isStaffChat
      ? (chat.customer_name || cid)
      : (chat.customer_name || ('Booking ' + (chat.booking_code || '')));
    setSelectedConvo({
      chatId: cid,
      name,
      isStaff: isStaffChat,
      category: chat.category,
      bookingCode: chat.booking_code,
      vessel: chat.vessel_name || ''
    });
    try {
      const msgRes = await getChatMessages(cid, undefined, undefined, 'el');
      setConvoMessages(msgRes.success ? (msgRes.messages || []) : []);
      await markChatMessagesRead(cid, currentStaff.code);
      setStaffUnread((prev) => ({ ...prev, [cid]: 0 }));
    } catch (e) {
      console.error('Error opening conversation:', e);
    }
  };

  const handleSendConvo = async (e) => {
    e.preventDefault();
    if (!convoInput.trim() || !selectedConvo) return;
    try {
      const result = await sendChatMessage({
        chat_id: selectedConvo.chatId,
        booking_code: selectedConvo.bookingCode,
        vessel_name: selectedConvo.vessel,
        customer_name: selectedConvo.name,
        sender_id: currentStaff.code,
        sender_name: currentStaff.name,
        sender_role: roleToChat(currentStaff.role),
        recipient_role: catToRecip(selectedConvo.category),
        category: selectedConvo.category,
        lang: 'el',
        message: convoInput.trim(),
        is_visitor: 0
      });
      if (result.success && result.message) {
        setConvoMessages((prev) => [...prev, result.message]);
        setConvoInput('');
      }
    } catch (err) {
      console.error('Error sending conversation message:', err);
    }
  };

  // Poll the open conversation; beep on incoming from the other party; keep it marked read.
  useEffect(() => {
    if (!isStaff || !selectedConvo || !isOpen) return;
    let cancelled = false;
    const poll = async () => {
      try {
        const res = await getChatMessages(selectedConvo.chatId, undefined, undefined, 'el');
        if (cancelled || !res || !res.success) return;
        const msgs = res.messages || [];
        setConvoMessages((prev) => {
          const hasNew = msgs.length !== prev.length
            && msgs.some((m) => m.sender_id !== currentStaff.code
              && !prev.find((pm) => String(pm.id) === String(m.id)));
          if (hasNew) { try { playNotify(); } catch (e) {} }
          return msgs;
        });
        markChatMessagesRead(selectedConvo.chatId, currentStaff.code);
      } catch (err) {
        console.error('Error polling conversation:', err);
      }
    };
    const interval = setInterval(poll, 3000);
    return () => { cancelled = true; clearInterval(interval); };
  }, [isStaff, selectedConvo, isOpen]);

  // KOMMATI 5b-ui: chat-list helpers (avatar color + relative time)
  const AVATAR_COLORS = ['bg-emerald-500', 'bg-blue-500', 'bg-purple-500', 'bg-pink-500', 'bg-amber-500', 'bg-cyan-500', 'bg-rose-500', 'bg-indigo-500'];
  const avatarColor = (name) => {
    const str = String(name || '?');
    let h = 0;
    for (let i = 0; i < str.length; i++) { h = (h * 31 + str.charCodeAt(i)) >>> 0; }
    return AVATAR_COLORS[h % AVATAR_COLORS.length];
  };
  const avatarLetter = (name) => {
    const str = String(name || '?').trim();
    return str ? str.charAt(0).toUpperCase() : '?';
  };
  const formatChatTime = (ts) => {
    if (!ts) return '';
    try {
      const d = new Date(String(ts).replace(' ', 'T').replace(/Z?$/, 'Z'));
      if (isNaN(d.getTime())) return '';
      const now = new Date();
      const fmt = new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/Athens', year: 'numeric', month: '2-digit', day: '2-digit' });
      const sameDay = fmt.format(d) === fmt.format(now);
      if (sameDay) return d.toLocaleTimeString('el-GR', { hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Athens' });
      return d.toLocaleDateString('el-GR', { day: '2-digit', month: '2-digit', timeZone: 'Europe/Athens' });
    } catch (e) { return ''; }
  };

  const formatTime = (timestamp: string) => {
    const date = new Date(String(timestamp).replace(' ', 'T').replace(/Z?$/, 'Z'));
    if (isNaN(date.getTime())) return '';
    const now = new Date();
    const fmt = new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/Athens', year: 'numeric', month: '2-digit', day: '2-digit' });
    const sameDay = fmt.format(date) === fmt.format(now);
    const hm = date.toLocaleTimeString('el-GR', { hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Athens' });
    if (sameDay) return hm;
    const dm = date.toLocaleDateString('el-GR', { day: '2-digit', month: '2-digit', timeZone: 'Europe/Athens' });
    return dm + ' ' + hm;
  };

  // KOMMATI 4: total staff unread across my staff chats (for the floating-button badge).
  const totalUnread = Object.values(staffUnread).reduce((a, b) => a + (Number(b) || 0), 0);

  return (
    <>
      {/* Floating Chat Button */}
      <button
        onClick={() => { unlockAudio(); setIsOpen(!isOpen); }}
        className={`fixed bottom-6 right-6 w-16 h-16 bg-gradient-to-r from-emerald-500 to-teal-600 text-white rounded-full shadow-2xl hover:from-emerald-600 hover:to-teal-700 transition-all hover:scale-110 flex items-center justify-center ${isStaff && totalUnread > 0 && !isOpen ? 'ring-4 ring-red-400 animate-pulse' : ''}`}
        style={{ transition: 'all 0.3s ease', zIndex: 9999 }}
      >
        {unreadCount > 0 && (
          <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs font-bold w-6 h-6 rounded-full flex items-center justify-center animate-pulse">
            {unreadCount}
          </span>
        )}
        {isStaff && totalUnread > 0 && !isOpen && (
          <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs font-bold w-6 h-6 rounded-full flex items-center justify-center animate-pulse">
            {totalUnread}
          </span>
        )}
        <span className="text-2xl">{isOpen ? '✕' : '💬'}</span>
      </button>

      {/* Staff view - shown to logged-in staff instead of the customer chat (added: staff branch) */}
      {isOpen && isStaff && (
        <div
          className="fixed bottom-24 right-6 w-96 h-[600px] bg-white rounded-2xl shadow-2xl flex flex-col overflow-hidden"
          style={{
            animation: 'slideUp 0.3s ease-out',
            maxWidth: 'calc(100vw - 3rem)',
            maxHeight: 'calc(100vh - 8rem)',
            zIndex: 9998
          }}
        >
          <div className="text-white p-4 flex items-center justify-between bg-gradient-to-r from-emerald-500 to-teal-600">
            <h3 className="font-bold text-lg">Επικοινωνία Προσωπικού</h3>
            <div className="flex items-center gap-1">
              <button
                onClick={() => setShowSoundSettings(true)}
                className="text-white hover:bg-white/20 rounded-lg p-2 transition-colors"
                title="Ήχος ειδοποίησης"
                type="button"
              >
                <span className="text-xl">🔔</span>
              </button>
              <button
                onClick={() => setIsOpen(false)}
                className="text-white hover:bg-white/20 rounded-lg p-2 transition-colors"
                type="button"
              >
                <span className="text-xl">✕</span>
              </button>
            </div>
          </div>

          <div className="flex border-b border-gray-200">
            <button
              onClick={() => setStaffTab('chats')}
              className={`flex-1 p-3 font-semibold transition-colors ${staffTab === 'chats' ? 'bg-emerald-50 text-emerald-700 border-b-2 border-emerald-500' : 'text-gray-600 hover:bg-gray-50'}`}
              type="button"
            >
              Συνομιλίες
            </button>
            <button
              onClick={() => setStaffTab('new')}
              className={`flex-1 p-3 font-semibold transition-colors ${staffTab === 'new' ? 'bg-emerald-50 text-emerald-700 border-b-2 border-emerald-500' : 'text-gray-600 hover:bg-gray-50'}`}
              type="button"
            >
              Νέα
            </button>
          </div>

          <div className="flex-1 overflow-y-auto p-4 bg-gradient-to-br from-slate-50 to-blue-50">
            {staffTab === 'chats' && !selectedConvo && (
              <div className="-mx-4">
                {allChats.length === 0 ? (
                  <div className="text-center text-gray-500 p-4">Καμία συνομιλία</div>
                ) : (
                  allChats.map((c) => {
                    const isStaffChat = String(c.chat_id || '').indexOf('staff-') === 0;
                    const cu = staffUnread[c.chat_id] != null ? staffUnread[c.chat_id] : (Number(c.unread_count) || 0);
                    const label = isStaffChat ? (c.customer_name || c.chat_id) : (c.customer_name || ('Booking ' + (c.booking_code || '')));
                    const lastMsg = String(c.last_message || '');
                    const preview = lastMsg.length > 35 ? lastMsg.substring(0, 35) + '...' : lastMsg;
                    return (
                      <button
                        key={c.chat_id}
                        onClick={() => openExistingChat(c)}
                        className={`w-full px-4 py-3 flex items-center gap-3 border-b border-gray-100 hover:bg-gray-50 transition-colors text-left ${cu > 0 ? 'chat-unread-blink' : ''}`}
                        type="button"
                      >
                        <span className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center text-white font-bold ${avatarColor(label)}`}>
                          {avatarLetter(label)}
                        </span>
                        <span className="flex-1 min-w-0">
                          <span className="flex items-center justify-between gap-2">
                            <span className="flex items-center gap-1 min-w-0">
                              <span className="font-semibold text-gray-800 truncate">{label}</span>
                              {!isStaffChat && c.vessel_name && c.vessel_name !== 'General Inquiry' && (
                                <span className="text-xs text-gray-400 truncate flex-shrink-0">· {c.vessel_name}</span>
                              )}
                              {isStaffChat && <span className="text-xs px-1 rounded bg-emerald-100 text-emerald-700 flex-shrink-0">Staff</span>}
                            </span>
                            <span className="text-xs text-gray-400 flex-shrink-0">{formatChatTime(c.last_message_at)}</span>
                          </span>
                          <span className="flex items-center justify-between gap-2 mt-0.5">
                            <span className={`text-xs truncate ${cu > 0 ? 'text-gray-700 font-semibold' : 'text-gray-400'}`}>{preview || '\u00a0'}</span>
                            {cu > 0 && (
                              <span className="bg-red-500 text-white text-xs font-bold px-2 py-0.5 rounded-full flex-shrink-0 animate-pulse">{cu}</span>
                            )}
                          </span>
                        </span>
                      </button>
                    );
                  })
                )}
              </div>
            )}

            {staffTab === 'chats' && selectedConvo && (
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <button onClick={() => setSelectedConvo(null)} className="text-gray-600 hover:bg-gray-100 rounded-lg p-1 transition-colors" type="button">←</button>
                  <span className="font-bold text-gray-800 truncate">{selectedConvo.name}</span>
                  <span className="text-xs px-2 py-1 rounded-full bg-gray-100 text-gray-600">{selectedConvo.isStaff ? 'Staff' : selectedConvo.category}</span>
                </div>

                <div className="space-y-2 mb-3">
                  {convoMessages.length === 0 ? (
                    <div className="text-center text-gray-500 p-4">Καμία συνομιλία ακόμα</div>
                  ) : (
                    convoMessages.map((msg) => {
                      const mine = msg.sender_id === currentStaff.code;
                      return (
                        <div key={msg.id} className={`flex ${mine ? 'justify-end' : 'justify-start'}`}>
                          <div className={`max-w-[80%] rounded-2xl px-3 py-2 ${mine ? 'bg-emerald-500 text-white' : 'bg-white text-gray-800 shadow-md'}`}>
                            <div className="text-xs opacity-80 mb-1 flex items-baseline gap-2">
                              <span>{msg.sender_name}</span>
                              <span className="opacity-80">{formatTime(msg.timestamp)}</span>
                            </div>
                            <p className="text-sm whitespace-pre-wrap break-words">{msg.message}</p>
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>

                <form onSubmit={handleSendConvo} className="flex gap-2">
                  <input
                    type="text"
                    value={convoInput}
                    onChange={(e) => setConvoInput(e.target.value)}
                    placeholder="Μήνυμα..."
                    className="flex-1 px-3 py-2 border-2 border-gray-300 rounded-xl focus:border-emerald-500 focus:outline-none text-sm bg-white"
                    style={{ color: '#1f2937', backgroundColor: '#ffffff' }}
                    autoComplete="off"
                  />
                  <button type="submit" disabled={!convoInput.trim()} className="px-4 py-2 bg-gradient-to-r from-emerald-500 to-teal-600 text-white rounded-xl font-semibold disabled:opacity-50 text-sm">Αποστολή</button>
                </form>
              </div>
            )}

            {staffTab === 'new' && (
              <div className="space-y-2">
                <div className="flex gap-2 mb-2">
                  <button
                    onClick={() => setNewMode('staff')}
                    className={`flex-1 py-2 rounded-lg text-sm font-semibold ${newMode === 'staff' ? 'bg-emerald-500 text-white' : 'bg-gray-100 text-gray-600'}`}
                    type="button"
                  >
                    Προσωπικό
                  </button>
                  <button
                    onClick={() => setNewMode('cust')}
                    className={`flex-1 py-2 rounded-lg text-sm font-semibold ${newMode === 'cust' ? 'bg-emerald-500 text-white' : 'bg-gray-100 text-gray-600'}`}
                    type="button"
                  >
                    Πελάτες
                  </button>
                </div>

                {newMode === 'staff' && (
                  <div className="space-y-2">
                    {staffList.length === 0 ? (
                      <div className="text-center text-gray-500 p-4">Φόρτωση...</div>
                    ) : (
                      staffList.map((emp) => (
                        <button
                          key={emp.code}
                          onClick={() => openStaffChat(emp)}
                          className="w-full p-3 rounded-xl shadow-sm bg-white hover:bg-gray-50 flex items-center justify-between transition-colors text-left"
                          type="button"
                        >
                          <span className="font-semibold text-gray-800">{emp.name}</span>
                          <span className="text-xs px-2 py-1 rounded-full bg-emerald-100 text-emerald-700">{emp.role}</span>
                        </button>
                      ))
                    )}
                  </div>
                )}

                {newMode === 'cust' && (
                  <div className="space-y-2">
                    <input
                      type="text"
                      value={custSearch}
                      onChange={(e) => setCustSearch(e.target.value)}
                      placeholder="Αναζήτηση (αριθμός, όνομα, σκάφος)"
                      className="w-full px-3 py-2 border-2 border-gray-300 rounded-xl focus:border-emerald-500 focus:outline-none text-sm bg-white"
                      style={{ color: '#1f2937', backgroundColor: '#ffffff' }}
                      autoComplete="off"
                    />
                    {custSearch.trim() === '' ? (
                      <div className="text-center text-gray-400 p-4 text-sm">Πληκτρολόγησε για αναζήτηση</div>
                    ) : custResults.length === 0 ? (
                      <div className="text-center text-gray-400 p-4 text-sm">Κανένα αποτέλεσμα</div>
                    ) : (
                      custResults.map((b, bi) => {
                        const num = stripNum(custNum(b));
                        const ves = String(custField(b, 'vesselName') || '');
                        const chName = (custField(b, 'chartererFirstName') + ' ' + custField(b, 'chartererLastName')).trim();
                        const skName = (custField(b, 'skipperFirstName') + ' ' + custField(b, 'skipperLastName')).trim();
                        const contacts = [];
                        if (chName && skName && chName.toLowerCase() !== skName.toLowerCase()) {
                          contacts.push({ name: chName, role: 'Ναυλωτής' });
                          contacts.push({ name: skName, role: 'Σκίπερ' });
                        } else if (chName) {
                          contacts.push({ name: chName, role: 'Ναυλωτής' });
                        } else if (skName) {
                          contacts.push({ name: skName, role: 'Σκίπερ' });
                        } else {
                          contacts.push({ name: 'Booking ' + num, role: '' });
                        }
                        return (
                          <div key={(custNum(b) || '') + '-' + bi} className="bg-white rounded-xl shadow-sm p-2">
                            <div className="text-xs text-gray-500 mb-1">#{num} • {ves}</div>
                            {contacts.map((ct, ci) => (
                              <button
                                key={ci}
                                onClick={() => openCustomerChat(b, ct.name)}
                                className="w-full p-2 rounded-lg hover:bg-gray-50 flex items-center justify-between text-left"
                                type="button"
                              >
                                <span className="font-semibold text-gray-800 text-sm">{ct.name}</span>
                                {ct.role && <span className="text-xs px-2 py-1 rounded-full bg-blue-100 text-blue-700">{ct.role}</span>}
                              </button>
                            ))}
                          </div>
                        );
                      })
                    )}
                  </div>
                )}
              </div>
            )}

            {staffTab === 'staff' && !selectedStaffChat && (
              <div className="space-y-2">
                {staffList.length === 0 ? (
                  <div className="text-center text-gray-500 p-4">Φόρτωση...</div>
                ) : (
                  staffList.map((emp) => {
                    const empChatId = staffChatIdFor(emp.code);
                    const empUnread = staffUnread[empChatId] || 0;
                    return (
                    <button
                      key={emp.code}
                      onClick={() => openStaffChat(emp)}
                      className={`w-full p-3 rounded-xl shadow-sm flex items-center justify-between transition-colors text-left ${empUnread > 0 ? 'bg-red-50 hover:bg-red-100 animate-pulse' : 'bg-white hover:bg-gray-50'}`}
                      type="button"
                    >
                      <span className={`font-semibold ${empUnread > 0 ? 'text-red-700' : 'text-gray-800'}`}>{emp.name}</span>
                      <span className="flex items-center gap-2">
                        {empUnread > 0 && (
                          <span className="bg-red-500 text-white text-xs font-bold w-5 h-5 rounded-full flex items-center justify-center">{empUnread}</span>
                        )}
                        <span className="text-xs px-2 py-1 rounded-full bg-emerald-100 text-emerald-700">{emp.role}</span>
                      </span>
                    </button>
                    );
                  })
                )}
              </div>
            )}

            {staffTab === 'staff' && selectedStaffChat && (
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <button
                    onClick={() => setSelectedStaffChat(null)}
                    className="text-gray-600 hover:bg-gray-100 rounded-lg p-1 transition-colors"
                    type="button"
                  >
                    ←
                  </button>
                  <span className="font-bold text-gray-800">{selectedStaffChat.peer.name}</span>
                  <span className="text-xs px-2 py-1 rounded-full bg-emerald-100 text-emerald-700">{selectedStaffChat.peer.role}</span>
                </div>

                <div className="space-y-2 mb-3">
                  {staffMessages.length === 0 ? (
                    <div className="text-center text-gray-500 p-4">Καμία συνομιλία ακόμα</div>
                  ) : (
                    staffMessages.map((msg) => {
                      const mine = msg.sender_id === currentStaff.code;
                      return (
                        <div key={msg.id} className={`flex ${mine ? 'justify-end' : 'justify-start'}`}>
                          <div className={`max-w-[80%] rounded-2xl px-3 py-2 ${mine ? 'bg-emerald-500 text-white' : 'bg-white text-gray-800 shadow-md'}`}>
                            <div className="text-xs opacity-80 mb-1 flex items-baseline gap-2">
                              <span>{msg.sender_name}</span>
                              <span className="opacity-80">{formatTime(msg.timestamp)}</span>
                            </div>
                            <p className="text-sm whitespace-pre-wrap break-words">{msg.message}</p>
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>

                <form onSubmit={handleSendStaffMessage} className="flex gap-2">
                  <input
                    type="text"
                    value={staffInput}
                    onChange={(e) => setStaffInput(e.target.value)}
                    placeholder="Μήνυμα..."
                    className="flex-1 px-3 py-2 border-2 border-gray-300 rounded-xl focus:border-emerald-500 focus:outline-none text-sm bg-white"
                    style={{ color: '#1f2937', backgroundColor: '#ffffff' }}
                    autoComplete="off"
                  />
                  <button
                    type="submit"
                    disabled={!staffInput.trim()}
                    className="px-4 py-2 bg-gradient-to-r from-emerald-500 to-teal-600 text-white rounded-xl font-semibold disabled:opacity-50 text-sm"
                  >
                    Αποστολή
                  </button>
                </form>
              </div>
            )}

            {staffTab === 'customers' && !selectedCustChat && (
              <div className="space-y-2">
                <input
                  type="text"
                  value={custSearch}
                  onChange={(e) => setCustSearch(e.target.value)}
                  placeholder="Αναζήτηση (αριθμός, όνομα, σκάφος)"
                  className="w-full px-3 py-2 border-2 border-gray-300 rounded-xl focus:border-emerald-500 focus:outline-none text-sm bg-white"
                  style={{ color: '#1f2937', backgroundColor: '#ffffff' }}
                  autoComplete="off"
                />
                {custSearch.trim() === '' ? (
                  <div className="text-center text-gray-400 p-4 text-sm">Πληκτρολόγησε για αναζήτηση</div>
                ) : custResults.length === 0 ? (
                  <div className="text-center text-gray-400 p-4 text-sm">Κανένα αποτέλεσμα</div>
                ) : (
                  custResults.map((b, bi) => {
                    const num = stripNum(custNum(b));
                    const ves = String(custField(b, 'vesselName') || '');
                    const chName = (custField(b, 'chartererFirstName') + ' ' + custField(b, 'chartererLastName')).trim();
                    const skName = (custField(b, 'skipperFirstName') + ' ' + custField(b, 'skipperLastName')).trim();
                    const contacts = [];
                    if (chName && skName && chName.toLowerCase() !== skName.toLowerCase()) {
                      contacts.push({ name: chName, role: 'Ναυλωτής' });
                      contacts.push({ name: skName, role: 'Σκίπερ' });
                    } else if (chName) {
                      contacts.push({ name: chName, role: 'Ναυλωτής' });
                    } else if (skName) {
                      contacts.push({ name: skName, role: 'Σκίπερ' });
                    } else {
                      contacts.push({ name: 'Booking ' + num, role: '' });
                    }
                    return (
                      <div key={(custNum(b) || '') + '-' + bi} className="bg-white rounded-xl shadow-sm p-2">
                        <div className="text-xs text-gray-500 mb-1">#{num} • {ves}</div>
                        {contacts.map((ct, ci) => (
                          <button
                            key={ci}
                            onClick={() => openCustomerChat(b, ct.name)}
                            className="w-full p-2 rounded-lg hover:bg-gray-50 flex items-center justify-between text-left"
                            type="button"
                          >
                            <span className="font-semibold text-gray-800 text-sm">{ct.name}</span>
                            {ct.role && <span className="text-xs px-2 py-1 rounded-full bg-blue-100 text-blue-700">{ct.role}</span>}
                          </button>
                        ))}
                      </div>
                    );
                  })
                )}
              </div>
            )}

            {staffTab === 'customers' && selectedCustChat && (
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <button
                    onClick={() => setSelectedCustChat(null)}
                    className="text-gray-600 hover:bg-gray-100 rounded-lg p-1 transition-colors"
                    type="button"
                  >
                    ←
                  </button>
                  <span className="font-bold text-gray-800">{selectedCustChat.peerName}</span>
                  <span className="text-xs px-2 py-1 rounded-full bg-blue-100 text-blue-700">{selectedCustChat.vessel}</span>
                </div>

                <div className="space-y-2 mb-3">
                  {custMessages.length === 0 ? (
                    <div className="text-center text-gray-500 p-4">Καμία συνομιλία ακόμα</div>
                  ) : (
                    custMessages.map((msg) => {
                      const mine = msg.sender_id === currentStaff.code;
                      return (
                        <div key={msg.id} className={`flex ${mine ? 'justify-end' : 'justify-start'}`}>
                          <div className={`max-w-[80%] rounded-2xl px-3 py-2 ${mine ? 'bg-emerald-500 text-white' : 'bg-white text-gray-800 shadow-md'}`}>
                            <div className="text-xs opacity-80 mb-1 flex items-baseline gap-2">
                              <span>{msg.sender_name}</span>
                              <span className="opacity-80">{formatTime(msg.timestamp)}</span>
                            </div>
                            <p className="text-sm whitespace-pre-wrap break-words">{msg.message}</p>
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>

                <form onSubmit={handleSendCustMessage} className="flex gap-2">
                  <input
                    type="text"
                    value={custInput}
                    onChange={(e) => setCustInput(e.target.value)}
                    placeholder="Μήνυμα..."
                    className="flex-1 px-3 py-2 border-2 border-gray-300 rounded-xl focus:border-emerald-500 focus:outline-none text-sm bg-white"
                    style={{ color: '#1f2937', backgroundColor: '#ffffff' }}
                    autoComplete="off"
                  />
                  <button
                    type="submit"
                    disabled={!custInput.trim()}
                    className="px-4 py-2 bg-gradient-to-r from-emerald-500 to-teal-600 text-white rounded-xl font-semibold disabled:opacity-50 text-sm"
                  >
                    Αποστολή
                  </button>
                </form>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Category Selector - Only for users WITH booking (non-visitors) */}
      {isOpen && !isStaff && !selectedCategory && !isVisitor && (
        <div
          className="fixed bottom-24 right-6 w-96 bg-white rounded-2xl shadow-2xl p-6"
          style={{
            animation: 'slideUp 0.3s ease-out',
            maxWidth: 'calc(100vw - 3rem)',
            zIndex: 9998
          }}
        >
          <h3 className="text-xl font-bold text-gray-800 mb-4">Select Category</h3>
          <p className="text-sm text-gray-600 mb-4">Who would you like to talk to?</p>

          <div className="space-y-3">
            <button
              onClick={() => { unlockAudio(); setSelectedCategory('TECHNICAL'); }}
              className="w-full p-4 bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 text-white rounded-xl flex items-center gap-3 transition-all"
            >
              <span className="text-2xl">🔧</span>
              <div className="text-left">
                <div className="font-bold">Technical Manager</div>
                <div className="text-xs text-blue-100">Technical boat matters</div>
              </div>
            </button>

            <button
              onClick={() => { unlockAudio(); setSelectedCategory('FINANCIAL'); }}
              className="w-full p-4 bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 text-white rounded-xl flex items-center gap-3 transition-all"
            >
              <span className="text-2xl">💰</span>
              <div className="text-left">
                <div className="font-bold">Financial Manager</div>
                <div className="text-xs text-green-100">Finance & Payments</div>
              </div>
            </button>

            <button
              onClick={() => { unlockAudio(); setSelectedCategory('BOOKING'); }}
              className="w-full p-4 bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white rounded-xl flex items-center gap-3 transition-all"
            >
              <span className="text-2xl">📅</span>
              <div className="text-left">
                <div className="font-bold">Booking Manager</div>
                <div className="text-xs text-purple-100">Bookings & Dates</div>
              </div>
            </button>

            <button
              onClick={() => { unlockAudio(); setSelectedCategory('ADMINISTRATOR'); }}
              className="w-full p-4 bg-gradient-to-r from-gray-600 to-gray-800 hover:from-gray-700 hover:to-gray-900 text-white rounded-xl flex items-center gap-3 transition-all"
            >
              <span className="text-2xl">👨‍💼</span>
              <div className="text-left">
                <div className="font-bold">Administrator</div>
                <div className="text-xs text-gray-200">Administration</div>
              </div>
            </button>
          </div>

          <button
            onClick={() => setIsOpen(false)}
            className="w-full mt-4 p-2 text-gray-600 hover:bg-gray-100 rounded-xl transition-colors"
          >
            Cancel
          </button>
        </div>
      )}

      {/* Chat Modal */}
      {isOpen && !isStaff && (selectedCategory || isVisitor) && (
        <div
          className="fixed bottom-24 right-6 w-96 h-[600px] bg-white rounded-2xl shadow-2xl flex flex-col overflow-hidden"
          style={{
            animation: 'slideUp 0.3s ease-out',
            maxWidth: 'calc(100vw - 3rem)',
            maxHeight: 'calc(100vh - 8rem)',
            zIndex: 9998
          }}
        >
          {/* Chat Header */}
          <div
            className={`text-white p-4 flex items-center justify-between ${
              isVisitor ? 'bg-gradient-to-r from-purple-500 to-pink-500' :
              selectedCategory === 'TECHNICAL' ? 'bg-gradient-to-r from-blue-500 to-cyan-500' :
              selectedCategory === 'FINANCIAL' ? 'bg-gradient-to-r from-green-500 to-emerald-500' :
              'bg-gradient-to-r from-purple-500 to-pink-500'
            }`}
          >
            <div className="flex items-center gap-2">
              {/* Back button - only for non-visitors */}
              {!isVisitor && (
                <button
                  onClick={handleBackToCategories}
                  className="text-white hover:bg-white/20 rounded-lg p-2 transition-colors"
                  title="Αλλαγή Κατηγορίας"
                  type="button"
                >
                  ←
                </button>
              )}
              <span className="text-2xl">
                {isVisitor && '📅'}
                {!isVisitor && selectedCategory === 'TECHNICAL' && '🔧'}
                {!isVisitor && selectedCategory === 'FINANCIAL' && '💰'}
                {!isVisitor && selectedCategory === 'BOOKING' && '📅'}
                {!isVisitor && selectedCategory === 'ADMINISTRATOR' && '👨‍💼'}
              </span>
              <div>
                <h3 className="font-bold text-lg">
                  {isVisitor && 'Contact'}
                  {!isVisitor && selectedCategory === 'TECHNICAL' && 'Technical Support'}
                  {!isVisitor && selectedCategory === 'FINANCIAL' && 'Financial Support'}
                  {!isVisitor && selectedCategory === 'BOOKING' && 'Booking Support'}
                  {!isVisitor && selectedCategory === 'ADMINISTRATOR' && 'Administrator'}
                </h3>
                <p className={`text-sm ${
                  isVisitor ? 'text-purple-100' :
                  selectedCategory === 'TECHNICAL' ? 'text-blue-100' :
                  selectedCategory === 'FINANCIAL' ? 'text-green-100' :
                  'text-purple-100'
                }`}>
                  {isVisitor && 'General Inquiry'}
                  {!isVisitor && selectedCategory === 'TECHNICAL' && 'Technical'}
                  {!isVisitor && selectedCategory === 'FINANCIAL' && 'Finance'}
                  {!isVisitor && selectedCategory === 'BOOKING' && 'Bookings'}
                  {!isVisitor && selectedCategory === 'ADMINISTRATOR' && 'Administration'}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-1">
              <button
                onClick={() => setShowSoundSettings(true)}
                className="text-white hover:bg-white/20 rounded-lg p-2 transition-colors"
                title="Ήχος ειδοποίησης"
                type="button"
              >
                <span className="text-xl">🔔</span>
              </button>
              <button
                onClick={() => {
                  setIsOpen(false);
                  setSelectedCategory(null);
                  setMessages([]);
                  setChatId(null);
                }}
                className="text-white hover:bg-white/20 rounded-lg p-2 transition-colors"
                type="button"
              >
                <span className="text-xl">✕</span>
              </button>
            </div>
          </div>

          {/* Messages Area */}
          <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-gradient-to-br from-slate-50 to-blue-50">
            {loading && (
              <div className="text-center p-4">
                <div className="animate-spin text-4xl mb-2">⏳</div>
                <p className="text-sm text-gray-600">Φόρτωση...</p>
              </div>
            )}

            {!loading && messages.length === 0 && (
              <div className="text-center p-4 bg-white/50 rounded-lg">
                <div className="text-4xl mb-2">👋</div>
                <p className="text-sm text-gray-600">
                  {isVisitor
                    ? 'Welcome! How can we help you?'
                    : 'Welcome! How can I help you?'
                  }
                </p>
              </div>
            )}

            {messages.map((msg) => {
              const isCustomer = msg.sender === 'CUSTOMER';

              return (
                <div
                  key={msg.id}
                  className={`flex ${isCustomer ? 'justify-end' : 'justify-start'}`}
                >
                  <div
                    className={`max-w-[80%] rounded-2xl px-4 py-2 ${
                      isCustomer
                        ? 'bg-gradient-to-r from-blue-500 to-blue-600 text-white'
                        : 'bg-white text-gray-800 shadow-md'
                    }`}
                  >
                    <div className="flex items-baseline gap-2 mb-1">
                      <span className={`font-semibold text-xs ${isCustomer ? 'text-white' : 'text-gray-900'}`}>
                        {msg.senderName}
                      </span>
                      <span className={`text-xs ${isCustomer ? 'text-blue-100' : 'text-gray-500'}`}>
                        {formatTime(msg.timestamp)}
                      </span>
                    </div>
                    <p className={`text-sm whitespace-pre-wrap break-words ${isCustomer ? 'text-white' : 'text-gray-800'}`}>
                      {msg.message}
                    </p>
                  </div>
                </div>
              );
            })}

            <div ref={messagesEndRef} />
          </div>

          {/* Input Area */}
          <div className="p-4 bg-white border-t border-gray-200">
            <form onSubmit={handleSendMessage} className="flex gap-2">
              <input
                type="text"
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                placeholder="Type your message..."
                className="flex-1 px-4 py-2 border-2 border-gray-300 rounded-xl focus:border-blue-500 focus:outline-none text-sm bg-white"
                style={{
                  color: '#1f2937',
                  backgroundColor: '#ffffff'
                }}
                disabled={sending || loading}
                autoComplete="off"
              />
              <button
                type="submit"
                disabled={!newMessage.trim() || sending || loading}
                className="px-4 py-2 bg-gradient-to-r from-emerald-500 to-teal-600 text-white rounded-xl font-semibold hover:from-emerald-600 hover:to-teal-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed text-sm"
              >
                {sending ? '...' : '📤'}
              </button>
            </form>
          </div>
        </div>
      )}

      {/* KOMMATI 3: sound settings panel (staff + customer) */}
      {isOpen && showSoundSettings && (
        <div
          className="fixed bottom-24 right-6 w-96 bg-white rounded-2xl shadow-2xl p-5 flex flex-col"
          style={{ maxWidth: 'calc(100vw - 3rem)', maxHeight: 'calc(100vh - 8rem)', zIndex: 10000 }}
        >
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-lg font-bold text-gray-800">Ήχος ειδοποίησης</h3>
            <button onClick={() => setShowSoundSettings(false)} className="text-gray-500 hover:bg-gray-100 rounded-lg p-1" type="button">
              <span className="text-xl">✕</span>
            </button>
          </div>

          <label className="flex items-center gap-2 mb-3 cursor-pointer">
            <input type="checkbox" checked={myMuted} onChange={(e) => setMyMuted(e.target.checked)} />
            <span className="text-sm font-semibold text-gray-700">Σίγαση</span>
          </label>

          <div className={`flex-1 overflow-y-auto space-y-1 mb-3 ${myMuted ? 'opacity-40 pointer-events-none' : ''}`} style={{ maxHeight: '40vh' }}>
            {soundList.length === 0 ? (
              <div className="text-center text-gray-500 p-4 text-sm">Καμία διαθέσιμη επιλογή</div>
            ) : (
              soundList.map((name) => (
                <div key={name} className={`flex items-center gap-2 p-2 rounded-lg ${mySound === name ? 'bg-emerald-50' : 'hover:bg-gray-50'}`}>
                  <input type="radio" name="notifSound" checked={mySound === name} onChange={() => setMySound(name)} />
                  <span className="flex-1 text-sm text-gray-700 truncate">{name}</span>
                  <button onClick={() => previewSound(name)} className="text-emerald-600 hover:bg-emerald-50 rounded-lg p-1" type="button" title="Προεπισκόπηση">
                    <span className="text-lg">▶</span>
                  </button>
                </div>
              ))
            )}
          </div>

          <div className="flex gap-2">
            <button onClick={saveSoundSettings} className="flex-1 px-4 py-2 bg-gradient-to-r from-emerald-500 to-teal-600 text-white rounded-xl font-semibold text-sm" type="button">
              Αποθήκευση
            </button>
            <button onClick={() => setShowSoundSettings(false)} className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-xl font-semibold text-sm" type="button">
              Κλείσιμο
            </button>
          </div>
        </div>
      )}

      {/* Animations */}
      <style>{`
        @keyframes slideUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        input::placeholder {
          color: #9ca3af !important;
          opacity: 1 !important;
        }
      `}</style>
    </>
  );
}
